import { useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useExportProperties, useGetProperties, useGetPropertyById } from '@practium/features/api';
import { BRAZILIAN_STATES, CULTURE_LABELS } from '../constants/properties.constants';
import { stateToUf } from '../utils/masks';
import type { AreaDTO, PropertiesTab, Property, PropertyPermissions, Sector } from '../types';

const PAGE_SIZE = 10;

export interface UsePropertiesPageProps {
	permissions: PropertyPermissions;
}

export interface UsePropertiesPageReturn {
	isDetail: boolean;
	activeTab: PropertiesTab;
	onTabChange: (tab: PropertiesTab) => void;
	selectedProperty: Property | null;
	filteredProperties: Property[];
	filteredAreas: (AreaDTO & { sectorCount: number })[];
	filteredSectors: Sector[];
	searchQuery: string;
	setSearchQuery: Dispatch<SetStateAction<string>>;
	sortAsc: boolean;
	toggleSort: () => void;
	filterValues: string[];
	setFilterValues: Dispatch<SetStateAction<string[]>>;
	filterGroups: FilterGroup[];
	page: number;
	setPage: Dispatch<SetStateAction<number>>;
	totalPages: number;
	isLoading: boolean;
	exportProperties: (format: 'pdf' | 'xlsx') => Promise<void>;
	permissions: PropertyPermissions;
	showSearch: boolean;
}

export const usePropertiesPage = ({ permissions }: UsePropertiesPageProps): UsePropertiesPageReturn => {
	const [searchParams, setSearchParams] = useSearchParams();
	const propertyId = searchParams.get('id');
	const isDetail = !!propertyId;
	const activeTab: PropertiesTab = isDetail
		? ((searchParams.get('tab') as PropertiesTab) || 'properties')
		: 'properties';

	const [searchQuery, setSearchQueryState] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValuesState] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const debouncedSearchQuery = useDebounce(searchQuery, 400);

	const stateFilter = filterValues.filter((value) => value.startsWith('state:')).map((value) => value.slice(6));
	const cultureFilter = filterValues.filter((value) => value.startsWith('culture:')).map((value) => value.slice(8));
	const areaIdFilter = filterValues.filter((value) => value.startsWith('area:')).map((value) => value.slice(5));
	const remoteFilter = searchQuery.trim() ? debouncedSearchQuery.trim() || undefined : undefined;
	const { data: properties, pagination, isLoading: isLoadingProperties } = useGetProperties({
		enabled: !isDetail,
		page,
		limit: PAGE_SIZE,
		filter: remoteFilter,
		state: stateFilter.length ? stateFilter : undefined,
		sortAsc,
	});
	const { data: detail, isLoading: isLoadingDetail } = useGetPropertyById(propertyId ?? '', {
		enabled: isDetail,
	});
	const exportMutation = useExportProperties();
	const selectedProperty = detail?.property ?? null;

	useEffect(() => {
		setSearchQueryState('');
		setFilterValuesState([]);
		setPage(1);
	}, [propertyId, activeTab]);

	const setSearchQuery: Dispatch<SetStateAction<string>> = (value) => {
		setSearchQueryState(value);
		setPage(1);
	};
	const setFilterValues: Dispatch<SetStateAction<string[]>> = (value) => {
		setFilterValuesState(value);
		setPage(1);
	};
	const toggleSort = () => {
		setSortAsc((value) => !value);
		setPage(1);
	};

	const onTabChange = (tab: PropertiesTab) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set('tab', tab);
			return next;
		});
	};

	const propertyAreas = useMemo(() => detail?.property.areas ?? [], [detail?.property.areas]);
	const propertySectors = useMemo(
		() => propertyAreas.flatMap((area) => area.sectors.map((sector): Sector => ({
			...sector,
			propId: sector.propId ?? propertyId ?? '',
			areaId: sector.areaId ?? area._id ?? '',
			areaName: area.name,
			propName: selectedProperty?.name ?? '',
			plantingDate: sector.plantingDate ?? '',
		}))),
		[propertyAreas, propertyId, selectedProperty?.name]
	);

	const filterGroups = useMemo<FilterGroup[]>(() => {
		if (!isDetail) {
			return [{
				label: 'Estado',
				options: BRAZILIAN_STATES.map((name) => ({ value: `state:${stateToUf(name)}`, label: name })),
			}];
		}
		if (activeTab === 'areas') {
			const cultures = [...new Set(propertyAreas.map((area) => area.culture).filter(Boolean))];
			return [{
				label: 'Cultura',
				options: cultures.map((culture) => ({ value: `culture:${culture}`, label: CULTURE_LABELS[culture] ?? culture })),
			}];
		}
		if (activeTab === 'sectors') {
			return [{
				label: 'Área',
				options: propertyAreas.filter((area) => area._id).map((area) => ({
					value: `area:${area._id}`,
					label: area.name,
				})).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
			}];
		}
		return [];
	}, [isDetail, activeTab, propertyAreas]);

	const processedAreas = useMemo(() => propertyAreas
		.filter((area) => (!remoteFilter || area.name.toLocaleLowerCase('pt-BR').includes(remoteFilter.toLocaleLowerCase('pt-BR')))
			&& (!cultureFilter.length || cultureFilter.includes(area.culture)))
		.map((area) => ({ ...area, sectorCount: area.sectors.length }))
		.sort((a, b) => (sortAsc ? 1 : -1) * a.name.localeCompare(b.name, 'pt-BR')),
		[propertyAreas, remoteFilter, filterValues, sortAsc]);
	const processedSectors = useMemo(() => propertySectors
		.filter((sector) => (!remoteFilter || sector.name.toLocaleLowerCase('pt-BR').includes(remoteFilter.toLocaleLowerCase('pt-BR')))
			&& (!areaIdFilter.length || areaIdFilter.includes(sector.areaId)))
		.sort((a, b) => (sortAsc ? 1 : -1) * a.name.localeCompare(b.name, 'pt-BR')),
		[propertySectors, remoteFilter, filterValues, sortAsc]);

	const activeListLength = activeTab === 'areas' ? processedAreas.length : activeTab === 'sectors' ? processedSectors.length : 1;
	const totalPages = isDetail ? Math.max(1, Math.ceil(activeListLength / PAGE_SIZE)) : Math.max(1, pagination?.totalPages ?? 1);
	const filteredProperties = isDetail ? (selectedProperty ? [selectedProperty] : []) : properties;
	const filteredAreas = processedAreas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
	const filteredSectors = processedSectors.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const exportProperties = async (format: 'pdf' | 'xlsx') => {
		const { file, filename } = await exportMutation.mutateAsync({
			format,
			filter: isDetail ? undefined : remoteFilter,
			state: !isDetail && stateFilter.length ? stateFilter : undefined,
			sortAsc,
		});
		const url = URL.createObjectURL(file);
		const link = document.createElement('a');
		try {
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
		} finally {
			link.remove();
			window.setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	};

	return {
		isDetail,
		activeTab,
		onTabChange,
		selectedProperty,
		filteredProperties,
		filteredAreas,
		filteredSectors,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading: isDetail ? isLoadingDetail : isLoadingProperties,
		exportProperties,
		permissions,
		showSearch: !isDetail || activeTab !== 'properties',
	};
};
